import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  SalesGoal, GoalProgress, UpsertGoalInput,
  listGoals, getProgress, upsertGoal, deleteGoal,
} from '../services/salesGoalsApi';

interface State {
  goals: SalesGoal[];
  progress: GoalProgress | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchGoals: (filters?: { sales_rep_id?: number; active_for?: string }) => Promise<void>;
  fetchProgress: (asOf?: string) => Promise<void>;
  upsert: (input: UpsertGoalInput) => Promise<SalesGoal>;
  remove: (id: number) => Promise<void>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useSalesGoalsStore = create<State>()(
  devtools(
    (set) => ({
      goals: [],
      progress: null,
      loading: false,
      saving: false,
      error: null,
      fetchGoals: async (filters) => {
        set({ loading: true, error: null });
        try { set({ goals: await listGoals(filters), loading: false }); }
        catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      fetchProgress: async (asOf) => {
        set({ loading: true, error: null });
        try { set({ progress: await getProgress(asOf), loading: false }); }
        catch (err) {
          // 404 means no goal active — surface as null progress, not an error
          const msg = toErr(err);
          if (msg.includes('NOT_FOUND') || msg.includes('No goal')) {
            set({ progress: null, loading: false, error: null });
          } else {
            set({ loading: false, error: msg });
          }
        }
      },
      upsert: async (input) => {
        set({ saving: true, error: null });
        try {
          const goal = await upsertGoal(input);
          set((s) => ({
            goals: [goal, ...s.goals.filter((g) => g.id !== goal.id)],
            saving: false,
          }));
          return goal;
        } catch (err) { set({ saving: false, error: toErr(err) }); throw err; }
      },
      remove: async (id) => {
        set({ saving: true, error: null });
        try {
          await deleteGoal(id);
          set((s) => ({ goals: s.goals.filter((g) => g.id !== id), saving: false }));
        } catch (err) { set({ saving: false, error: toErr(err) }); throw err; }
      },
    }),
    { name: 'sales-goals-store' }
  )
);
